/**
 * TypeScript view of the `nurikabe` format.
 * Source of truth: `shared/schema/nurikabe.{definition,submission,solution,progress}.json`.
 */

export type Localized = Record<string, string | undefined>;

/** The size of the island a cell belongs to, or null for a cell without a number. */
export type Clue = number | null;

export interface NurikabeDefinition {
  puzzleType: "nurikabe";
  typeVersion: number;
  rows: number;
  cols: number;
  /** One array per row. */
  clues: Clue[][];
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

export const SHADED = "#";
export const EMPTY = ".";
/** A solving aid: the player is sure the cell stays unshaded. Never sent to the server. */
export const DOT = "x";
export type Mark = typeof SHADED | typeof DOT | typeof EMPTY;

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface NurikabeProgress {
  version: 1;
  /** One string per row, `#` shaded, `x` dotted, `.` undecided. */
  rows: string[];
}

/** One string per row, `#` shaded and `.` unshaded: dots are folded into `.`. */
export interface NurikabeSubmission {
  rows: string[];
}

/** The author's wall, in the submission format. */
export type NurikabeSolution = NurikabeSubmission;

export const MAX_SIZE = 30;
export const MAX_CLUE = 999;

export type Cell = [row: number, col: number];

export function cellId(row: number, col: number): string {
  return `r${row}c${col}`;
}

/** Picks the viewer's language, falling back to the base language, then English, then any. */
export function localized(value: Localized | null | undefined, locale: string): string {
  if (!value) return "";
  return (
    value[locale] ??
    value[locale.split("-")[0]] ??
    value.en ??
    Object.values(value).find((v) => typeof v === "string" && v.length > 0) ??
    ""
  );
}
